/**
 * Visual permissions matrix (admin-only).
 * Click any cell to rotate through the 3 states:
 *   Obligatorio (verde) → Editable (amarillo) → No aplica (gris)
 * "Guardar cambios" applies pending updates in one bulk call.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, RotateCcw, Save, Sparkles, CheckCircle2, MinusCircle,
  Pencil, Loader2,
} from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";

const ROLES = ["admin", "manager", "seller"];
const ROLE_LABELS = {
  admin:   { es: "Administrador", en: "Administrator" },
  manager: { es: "Manager", en: "Manager" },
  seller:  { es: "Vendedor", en: "Seller" },
};
const STATES = ["required", "editable", "na"];
const nextState = (s) => STATES[(STATES.indexOf(s) + 1) % STATES.length];

const STATE_META = {
  required: {
    labelEs: "Obligatorio", labelEn: "Required",
    Icon: CheckCircle2, bg: "bg-canales-blue", text: "text-white", border: "border-canales-blue",
  },
  editable: {
    labelEs: "Editable por usuario", labelEn: "Editable per user",
    Icon: Pencil, bg: "bg-canales-yellow", text: "text-black", border: "border-canales-yellow",
  },
  na: {
    labelEs: "No aplica", labelEn: "Not applicable",
    Icon: MinusCircle, bg: "bg-neutral-200", text: "text-neutral-500", border: "border-neutral-200",
  },
};

const StateChip = ({ state, active, onClick }) => {
  const meta = STATE_META[state];
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 border-2 border-black font-display font-black text-xs uppercase tracking-widest ${meta.bg} ${meta.text} ${
        active ? "" : "opacity-40 hover:opacity-100"
      }`}
    >
      <meta.Icon className="w-4 h-4" />
      {meta.labelEs}
    </button>
  );
};

const Cell = ({ state, onCycle, testId }) => {
  const meta = STATE_META[state];
  return (
    <button
      onClick={onCycle}
      data-testid={testId}
      title={STATES.map((s) => STATE_META[s].labelEs).join(" → ")}
      className={`inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-black transition-transform hover:scale-110 ${meta.bg} ${meta.text}`}
    >
      <meta.Icon className="w-5 h-5" />
    </button>
  );
};

export default function Permissions() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/permissions");
      setItems(data.items || []);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando permisos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cycle = (key, role) => {
    setItems((prev) =>
      prev.map((it) =>
        it.key === key
          ? { ...it, states: { ...it.states, [role]: nextState(it.states[role]) } }
          : it
      )
    );
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const changes = [];
      for (const it of items) {
        for (const r of ROLES) {
          changes.push({ key: it.key, role: r, state: it.states[r] });
        }
      }
      await api.put("/admin/permissions", { changes });
      toast.success(lang === "es" ? "Permisos guardados" : "Permissions saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm(lang === "es" ? "¿Restaurar valores por defecto?" : "Reset to defaults?")) return;
    setSaving(true);
    try {
      await api.post("/admin/permissions/reset");
      await load();
      toast.success(lang === "es" ? "Valores por defecto restaurados" : "Defaults restored");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const totalPerms = items.length;

  const legend = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-3 mt-3">
        {STATES.map((s) => (
          <StateChip key={s} state={s} active />
        ))}
      </div>
    ),
    []
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-permissions-page"
    >
      <div className="brutal p-6 bg-canales-blue text-white flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 border-2 border-white flex items-center justify-center">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <div>
            <div className="text-xs font-display font-black uppercase tracking-widest opacity-80">
              {lang === "es" ? "Permisos" : "Permissions"}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-4xl flex items-center gap-3">
              {lang === "es" ? "Permisos por rol" : "Permissions by role"}
              <span className="text-xs bg-canales-yellow text-black px-2 py-1 rounded-full inline-flex items-center gap-1 border-2 border-black">
                <Sparkles className="w-3 h-3" /> {lang === "es" ? "Personalizada" : "Custom"}
              </span>
            </h1>
            <p className="mt-2 text-sm opacity-90 max-w-2xl">
              {lang === "es" ? (
                <>
                  Haz <b>clic en cualquier celda</b> para rotar entre <b>Obligatorio → Editable → No aplica</b> y presiona &quot;Guardar cambios&quot; para aplicar.
                </>
              ) : (
                <>
                  <b>Click any cell</b> to rotate <b>Required → Editable → Not applicable</b> then press &quot;Save changes&quot;.
                </>
              )}
            </p>
            {legend}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            onClick={resetDefaults}
            disabled={saving || loading}
            data-testid="perm-reset-btn"
            className="brutal-btn bg-white text-black px-4 py-2 flex items-center gap-2 disabled:opacity-40"
          >
            <RotateCcw className="w-4 h-4" />
            {lang === "es" ? "Restaurar valores por defecto" : "Reset to defaults"}
          </button>
          <div className="text-xs opacity-75 font-body">
            {totalPerms} {lang === "es" ? "permisos · 3 roles · clic para editar" : "permissions · 3 roles · click to edit"}
          </div>
        </div>
      </div>

      <div className="brutal bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin inline" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-canales-cream border-b-2 border-black">
                <tr className="text-xs uppercase font-display font-black tracking-widest text-neutral-600">
                  <th className="text-left px-6 py-4">{lang === "es" ? "Permiso" : "Permission"}</th>
                  {ROLES.map((r) => (
                    <th key={r} className="text-center px-6 py-4">
                      {ROLE_LABELS[r][lang]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.key}
                    data-testid={`perm-row-${it.key}`}
                    className="border-b border-neutral-200 hover:bg-canales-cream/40 transition-colors"
                  >
                    <td className="px-6 py-4 max-w-md">
                      <div className="font-display font-bold text-base">
                        {it[`label_${lang}`] || it.label_es}
                      </div>
                      <div className="text-xs text-neutral-600 font-body mt-1">
                        {it[`description_${lang}`] || it.description_es}
                      </div>
                    </td>
                    {ROLES.map((r) => (
                      <td key={r} className="px-6 py-4 text-center">
                        <Cell
                          state={it.states[r] || "na"}
                          onCycle={() => cycle(it.key, r)}
                          testId={`perm-cell-${it.key}-${r}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 flex justify-center">
        <motion.button
          initial={false}
          animate={{ opacity: dirty ? 1 : 0.4, y: dirty ? 0 : 6 }}
          disabled={!dirty || saving}
          onClick={save}
          data-testid="perm-save-btn"
          className="brutal-btn bg-canales-red text-white px-6 py-3 flex items-center gap-2 font-display font-black text-lg disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {dirty
            ? lang === "es" ? "Guardar cambios" : "Save changes"
            : lang === "es" ? "Sin cambios pendientes" : "No pending changes"}
        </motion.button>
      </div>
    </motion.div>
  );
}
